<script setup lang="ts">
import { defineComponent, h, markRaw, watch } from "vue"
import { storeToRefs } from "pinia"
import { useToast } from "@nuxt/ui/composables/useToast"
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome"
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core"
import {
  faCircleInfo,
  faCircleCheck,
  faTriangleExclamation,
  faCircleXmark,
  faXmark
} from "@fortawesome/free-solid-svg-icons"
import { useToastStore } from "@/store/toast.store"
import { AlertTypes } from "@/interfaces/component/alert.interface"

// UApp renders the notifications through its toaster.
defineOptions({ render: () => null })

const toast = useToast()
const toastStore = useToastStore()
const { pendingToasts } = storeToRefs(toastStore)

function fontAwesomeIcon(icon: IconDefinition) {
  return markRaw(
    defineComponent({
      setup: () => () => h(FontAwesomeIcon, { icon, "aria-hidden": true })
    })
  )
}

const closeIcon = fontAwesomeIcon(faXmark)
const appearances = {
  [AlertTypes.NEUTRAL]: { color: "info", icon: fontAwesomeIcon(faCircleInfo) },
  [AlertTypes.SUCCESS]: { color: "success", icon: fontAwesomeIcon(faCircleCheck) },
  [AlertTypes.WARNING]: { color: "warning", icon: fontAwesomeIcon(faTriangleExclamation) },
  [AlertTypes.ERROR]: { color: "error", icon: fontAwesomeIcon(faCircleXmark) }
} as const

watch(
  pendingToasts,
  (pending) => {
    if (!pending.length) return

    for (const pendingToast of toastStore.takeToasts()) {
      const action = pendingToast.action
      const notification = toast.add({
        ...appearances[pendingToast.type],
        title: pendingToast.title,
        description: pendingToast.body,
        duration: Math.max(0, pendingToast.lifetime ?? 5000),
        close: true,
        closeIcon,
        actions: action
          ? [
              {
                label: action.label,
                closeOnClick: false,
                loadingAuto: true,
                async onClick() {
                  await action.handler()
                  toast.remove(notification.id)
                }
              }
            ]
          : undefined
      })
    }
  },
  { immediate: true }
)
</script>
